import React from "react";
import { Form, Input, Select, Upload, Divider, Tag, Tooltip, Button } from "antd";
import { RiLoaderLine, RiAddLine, RiCloseLine } from "react-icons/ri";

const { TextArea } = Input;

// Common emoji icons for copilots
const EMOJI_OPTIONS = [
  "📊", "📝", "⚖️", "🏥", "💰", "✍️", "📖", "👨‍💻", "🚀", "📧",
  "🎧", "🔬", "🌍", "🔍", "📢", "🎨", "✨", "🏷️", "🖼️", "🎯",
  "📋", "💡", "🤝", "📈", "🐍", "🔎", "👥", "🌟", "🎪", "📖",
  "🔒", "🗓️", "📓", "🎤", "💬", "📚", "💼", "💻", "⚡", "🎨"
];

// Common color options
const COLOR_OPTIONS = [
  "#217346", "#4285f4", "#1e3a5f", "#dc2626", "#059669", "#7c3aed",
  "#be185d", "#0ea5e9", "#ea580c", "#0891b2", "#4f46e5", "#0d9488",
  "#16a34a", "#f59e0b", "#e11d48", "#8b5cf6", "#ec4899", "#a259ff",
  "#fbbf24", "#3776ab", "#06b6d4", "#6366f1", "#a855f7", "#14b8a6"
];

// Available AI models
const AI_MODELS = [
  { value: "gpt-4o", label: "GPT-4o (Recommended)" },
  { value: "gpt-4o-mini", label: "GPT-4o Mini" },
  { value: "gpt-4-turbo", label: "GPT-4 Turbo" },
  { value: "gpt-4", label: "GPT-4" },
  { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo" },
  { value: "claude-3-5-sonnet-20241022", label: "Claude 3.5 Sonnet" },
  { value: "claude-3-opus-20240229", label: "Claude 3 Opus" },
  { value: "claude-3-haiku-20240307", label: "Claude 3 Haiku" },
];

const LeftCol = ({
  loading,
  values,
  setValues,
  handleFormChange,
  plans,
  categories = [],
  handleUploadImage,
  beforeImageUpload,
}) => {
  const uploadButton = (
    <div>
      {loading ? (
        <RiLoaderLine
          className="remix-icon-loading remix-icon-spin remix-icon"
          size={24}
        />
      ) : (
        <RiAddLine size={24} />
      )}
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  // Handle adding a new tag
  const handleAddTag = (e) => {
    if (e.key === "Enter" && e.target.value.trim()) {
      e.preventDefault();
      const newTag = e.target.value.trim().toLowerCase();
      if (!values.tags?.includes(newTag)) {
        setValues({
          ...values,
          tags: [...(values.tags || []), newTag],
        });
      }
      e.target.value = "";
    }
  };

  // Handle removing a tag
  const handleRemoveTag = (tagToRemove) => {
    setValues({
      ...values,
      tags: values.tags?.filter((tag) => tag !== tagToRemove) || [],
    });
  };

  // Handle adding a suggested question
  const handleAddQuestion = () => {
    setValues({
      ...values,
      suggestedQuestions: [...(values.suggestedQuestions || []), ""],
    });
  };

  // Handle updating a suggested question
  const handleUpdateQuestion = (index, value) => {
    const updatedQuestions = [...(values.suggestedQuestions || [])];
    updatedQuestions[index] = value;
    setValues({
      ...values,
      suggestedQuestions: updatedQuestions,
    });
  };

  // Handle removing a suggested question
  const handleRemoveQuestion = (index) => {
    const updatedQuestions = [...(values.suggestedQuestions || [])];
    updatedQuestions.splice(index, 1);
    setValues({
      ...values,
      suggestedQuestions: updatedQuestions,
    });
  };

  return (
    <>
      <Form
        labelCol={{
          span: 6,
        }}
        wrapperCol={{
          span: 17,
        }}
        layout="horizontal"
        size="middle"
      >
        {/* Identity Section */}
        <Divider orientation="left" style={{ marginTop: 0 }}>
          <span style={{ fontSize: "14px", fontWeight: 600 }}>Identity</span>
        </Divider>

        {/* Image */}
        <Form.Item label="Image">
          <Upload
            name="image"
            listType="picture-card"
            customRequest={handleUploadImage}
            className="avatar-uploader"
            showUploadList={false}
            beforeUpload={beforeImageUpload}
            multiple={false}
            accept="image/*,image/gif"
          >
            {values.image ? (
              <img src={values.image} alt="avatar" style={{ width: "100%" }} />
            ) : (
              uploadButton
            )}
          </Upload>
        </Form.Item>

        {/* Icon & Color Row */}
        <Form.Item label="Icon">
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <Select
              value={values.icon || "📊"}
              onChange={(value) => setValues({ ...values, icon: value })}
              style={{ width: 80 }}
              dropdownStyle={{ padding: "8px" }}
              dropdownRender={(menu) => (
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(8, 1fr)",
                  gap: "4px",
                  padding: "8px"
                }}>
                  {EMOJI_OPTIONS.map((emoji) => (
                    <div
                      key={emoji}
                      onClick={() => setValues({ ...values, icon: emoji })}
                      style={{
                        width: "32px",
                        height: "32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        borderRadius: "4px",
                        fontSize: "18px",
                        backgroundColor: values.icon === emoji ? "#e6f7ff" : "transparent",
                        border: values.icon === emoji ? "1px solid #1890ff" : "1px solid transparent",
                      }}
                    >
                      {emoji}
                    </div>
                  ))}
                </div>
              )}
            >
              <Select.Option value={values.icon || "📊"}>
                <span style={{ fontSize: "18px" }}>{values.icon || "📊"}</span>
              </Select.Option>
            </Select>

            {/* Color */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "12px", color: "#666" }}>Color:</span>
              <Select
                value={values.color || "#6366f1"}
                onChange={(value) => setValues({ ...values, color: value })}
                style={{ width: 100 }}
                dropdownStyle={{ padding: "8px" }}
                dropdownRender={() => (
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(6, 1fr)",
                    gap: "6px",
                    padding: "8px"
                  }}>
                    {COLOR_OPTIONS.map((color) => (
                      <Tooltip title={color} key={color}>
                        <div
                          onClick={() => setValues({ ...values, color: color })}
                          style={{
                            width: "28px",
                            height: "28px",
                            borderRadius: "4px",
                            backgroundColor: color,
                            cursor: "pointer",
                            border: values.color === color ? "2px solid #1890ff" : "2px solid transparent",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.1)"
                          }}
                        />
                      </Tooltip>
                    ))}
                  </div>
                )}
              >
                <Select.Option value={values.color || "#6366f1"}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px"
                  }}>
                    <div style={{
                      width: "16px",
                      height: "16px",
                      borderRadius: "3px",
                      backgroundColor: values.color || "#6366f1"
                    }} />
                    <span style={{ fontSize: "11px" }}>{values.color || "#6366f1"}</span>
                  </div>
                </Select.Option>
              </Select>
            </div>
          </div>
        </Form.Item>

        {/* Name */}
        <Form.Item label="Name" required>
          <Input
            name="name"
            value={values.name}
            onChange={handleFormChange}
            placeholder="e.g., Excel Expert, Legal Advisor"
            maxLength={50}
          />
        </Form.Item>

        {/* Short Description */}
        <Form.Item label="Short Desc">
          <Input
            name="shortDescription"
            value={values.shortDescription}
            onChange={handleFormChange}
            placeholder="Brief tagline (shown in cards)"
            maxLength={80}
            showCount
          />
        </Form.Item>

        {/* Category */}
        <Form.Item label="Category">
          <Select
            value={values.category}
            onChange={(value) => setValues({ ...values, category: value })}
            placeholder="Select a category"
            allowClear
          >
            {categories.map((cat) => (
              <Select.Option key={cat._id} value={cat._id}>
                <span>{cat.icon} {cat.name}</span>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* Tags */}
        <Form.Item label="Tags">
          <div>
            <div style={{ marginBottom: "8px", display: "flex", flexWrap: "wrap", gap: "4px" }}>
              {values.tags?.map((tag) => (
                <Tag
                  key={tag}
                  closable
                  onClose={() => handleRemoveTag(tag)}
                  style={{ marginRight: 0 }}
                >
                  {tag}
                </Tag>
              ))}
            </div>
            <Input
              placeholder="Type and press Enter to add tags"
              onKeyDown={handleAddTag}
              size="small"
            />
          </div>
        </Form.Item>

        {/* Plan */}
        <Form.Item label="Plans">
          <Select
            mode="multiple"
            name="plans"
            placeholder="Select plans"
            value={values.plans}
            onChange={(value) => setValues({ ...values, plans: value })}
          >
            {plans.map((plan) => (
              <Select.Option key={plan._id} value={plan.name}>
                {plan.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* Order */}
        <Form.Item label="Order">
          <Input
            name="order"
            type="number"
            placeholder="Display order (lower = first)"
            onChange={handleFormChange}
            value={values.order}
            style={{ width: "120px" }}
          />
        </Form.Item>

        {/* Content Section */}
        <Divider orientation="left">
          <span style={{ fontSize: "14px", fontWeight: 600 }}>Content</span>
        </Divider>

        {/* Description */}
        <Form.Item label="Description">
          <TextArea
            name="description"
            value={values.description}
            onChange={handleFormChange}
            placeholder="Detailed description of what this copilot does"
            rows={2}
            maxLength={500}
            showCount
          />
        </Form.Item>

        {/* System Prompt / Command */}
        <Form.Item label="System Prompt" required>
          <TextArea
            name="command"
            value={values.command}
            onChange={handleFormChange}
            placeholder="The system prompt that defines the copilot's behavior and expertise..."
            rows={6}
            style={{ fontFamily: "monospace", fontSize: "12px" }}
          />
        </Form.Item>

        {/* Welcome Message */}
        <Form.Item label="Welcome Msg">
          <TextArea
            name="welcomeMessage"
            value={values.welcomeMessage}
            onChange={handleFormChange}
            placeholder="First message shown to users when they start a chat"
            rows={2}
            maxLength={300}
          />
        </Form.Item>

        {/* Suggested Questions */}
        <Form.Item label="Suggestions">
          <div>
            {values.suggestedQuestions?.map((question, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  gap: "8px",
                  marginBottom: "8px",
                  alignItems: "center"
                }}
              >
                <Input
                  value={question}
                  onChange={(e) => handleUpdateQuestion(index, e.target.value)}
                  placeholder={`Suggested question ${index + 1}`}
                  size="small"
                />
                <Button
                  type="text"
                  danger
                  icon={<RiCloseLine size={16} />}
                  onClick={() => handleRemoveQuestion(index)}
                  size="small"
                />
              </div>
            ))}
            {(values.suggestedQuestions?.length || 0) < 4 && (
              <Button
                type="dashed"
                onClick={handleAddQuestion}
                icon={<RiAddLine size={14} />}
                size="small"
                style={{ width: "100%" }}
              >
                Add Suggested Question
              </Button>
            )}
          </div>
        </Form.Item>

        {/* AI Configuration Section */}
        <Divider orientation="left">
          <span style={{ fontSize: "14px", fontWeight: 600 }}>AI Configuration</span>
        </Divider>

        {/* Model */}
        <Form.Item label="Model">
          <Select
            value={values.model || "gpt-4o"}
            onChange={(value) => setValues({ ...values, model: value })}
            style={{ width: "100%" }}
          >
            {AI_MODELS.map((model) => (
              <Select.Option key={model.value} value={model.value}>
                {model.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* Temperature & Max Tokens Row */}
        <Form.Item label="Temperature">
          <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <Input
              name="temperature"
              type="number"
              min="0"
              max="2"
              step="0.1"
              onChange={handleFormChange}
              value={values.temperature}
              style={{ width: "100px" }}
            />
            <span style={{ fontSize: "12px", color: "#666" }}>Max Tokens:</span>
            <Input
              name="max_tokens"
              type="number"
              placeholder="2000"
              onChange={handleFormChange}
              value={values.max_tokens}
              style={{ width: "100px" }}
            />
          </div>
        </Form.Item>

        {/* Advanced Settings */}
        <Form.Item label="Top P">
          <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <Input
              name="top_p"
              type="number"
              min="0"
              max="1"
              step="0.01"
              onChange={handleFormChange}
              value={values.top_p}
              style={{ width: "80px" }}
            />
            <span style={{ fontSize: "12px", color: "#666" }}>Freq Penalty:</span>
            <Input
              name="frequency_penalty"
              type="number"
              min="0"
              max="2"
              step="0.1"
              onChange={handleFormChange}
              value={values.frequency_penalty}
              style={{ width: "80px" }}
            />
            <span style={{ fontSize: "12px", color: "#666" }}>Pres Penalty:</span>
            <Input
              name="presence_penalty"
              type="number"
              min="0"
              max="2"
              step="0.1"
              onChange={handleFormChange}
              value={values.presence_penalty}
              style={{ width: "80px" }}
            />
          </div>
        </Form.Item>
      </Form>
    </>
  );
};

export default LeftCol;
